'use client'

import { MobileSkeletonLoader } from '@/components/atoms/MobileSkeletonLoader/MobileSkeletonLoader'
import { ImageWithFallback } from '@/components/molecules/ImageLoader/ImageLoader'
import { MobileBlockHeader } from '@/components/molecules/MobileBlockHeader/MobileBlockHeader'
import { useCardsRoute } from '@/features/Cards/cards.hooks'
import { Card } from '@/features/Cards/cards.types'
import { CardContextActions } from '@/features/Cards/ui/CardContextActions/CardContextActions'
import { useModal } from '@/features/Modal/Modal.hooks'
import { AppRoutes } from '@/routes'
import { getCdnImageWithPath } from '@/shared/helpers/cdn'
import { formatUsdAmount } from '@/shared/helpers/currency'
import { useBalancesStore } from '@/store'
import { Splide, SplideSlide } from '@splidejs/react-splide'
import { Button, Flex } from 'antd'
import { useRouter } from 'nextjs-toploader/app'
import React, { useCallback, useMemo, useRef } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { getStorageKey } from '@/shared/helpers/localStorage'
import { useQuery } from '@tanstack/react-query'
import { createCanIssueCardsQueryOptions, createCardsQueryOptions } from '@/features/Cards/queries'
import { createUserProfileQueryOptions } from '@/features/User/queries'
import { isKycApproved, isKycNotStarted, isKycPendingReview, isKycRejected, isKycStarted } from '@/features/User/user.selectors'
import styles from './MobileCards.module.scss'
import CardImageSvg from '../../assets/card.svg'
import CardImageBig from '../../assets/card-ms.svg'

type CountryAcceptedStatus = 'idle' | 'yes' | 'no'

interface CardStateConfig {
  icon: string
  message: string
  buttonText?: string
  buttonAction?: () => void
}

export function MobileCards() {
  const sliderRef = useRef(null)
  const router = useRouter()
  const [acceptedCountry] = useLocalStorage<CountryAcceptedStatus>(getStorageKey('godefi:country:accepted'), 'idle')

  const { createModal } = useModal()
  const { route: cardsRoute } = useCardsRoute()
  const { data: profile } = useQuery(createUserProfileQueryOptions())
  const { data: cards, isLoading: isLoadingCards } = useQuery(createCardsQueryOptions(true))
  const { data: canIssueData, isLoading: isLoadingCanIssue } = useQuery(createCanIssueCardsQueryOptions(true))

  const selectedWallet = useBalancesStore((state) => state.selectedWallet)
  const canIssueCard = canIssueData?.canIssue ?? false
  const canIssueCardReason = canIssueData?.reason ?? []
  const isLoading = isLoadingCards || isLoadingCanIssue || !selectedWallet
  const isFeatureDisabled = !canIssueCard && canIssueCardReason.includes('CARDS_FEATURE_DISABLED')

  const filteredCards = useMemo(() => {
    if (!cards?.length || !selectedWallet) return []
    return cards.filter((card) => card.solanaAddress === selectedWallet.address)
  }, [cards, selectedWallet])

  const handleCardOpen = useCallback(() => {
    router.push(cardsRoute)
  }, [router, cardsRoute])

  const handleKycRetry = useCallback(() => {
    router.push(AppRoutes.KYC_V2_PAGE)
  }, [router])

  const handleContextMenu = useCallback((card: Card) => {
    createModal(<CardContextActions card={card} />)
  }, [createModal])

  const getCardStateConfig = (): CardStateConfig | null => {
    const hasCards = filteredCards.length > 0

    if (!hasCards && acceptedCountry === 'no' && isKycNotStarted(profile)) {
      return {
        icon: '⏳',
        message: "Your application is accepted. We'll notify you via push and email once it's approved."
      }
    }

    if (!hasCards && acceptedCountry === 'yes' && isKycNotStarted(profile)) {
      return {
        icon: '✅',
        message: 'Your application is successfully approved',
        buttonText: 'Continue to KYC',
        buttonAction: handleCardOpen
      }
    }

    if (!hasCards && (isKycNotStarted(profile) || isKycStarted(profile))) {
      return {
        icon: '',
        message: 'Your first card in 5 steps',
        buttonText: 'Open card',
        buttonAction: handleCardOpen
      }
    }

    // if (!hasCards && isKycStarted(profile)) {
    //   return {
    //     icon: '',
    //     message: 'Go through identification and get your first card',
    //     buttonText: 'Pass KYC',
    //     buttonAction: handleCardOpen
    //   }
    // }

    if (!hasCards && isKycApproved(profile)) {
      return {
        icon: '✅',
        message: 'Your application is successfully approved',
        buttonText: 'Get the card',
        buttonAction: handleCardOpen
      }
    }

    if (!hasCards && isKycPendingReview(profile)) {
      return {
        icon: '⏳',
        message: 'Identity verification is in progress and may take up to 24 hours'
      }
    }

    if (!hasCards && isKycRejected(profile)) {
      return {
        icon: '🛑',
        message: 'Your document has been rejected',
        buttonText: 'Try again',
        buttonAction: handleKycRetry
      }
    }

    return null
  }

  const renderCardState = (config: CardStateConfig) => (
    <div className={styles.issueCard}>
      <Flex align="center" gap={25}>
        {config.icon ? (
          <div className={styles.cardImg}>
            <span className={styles.cardImgIcon}>{config.icon}</span>
            <CardImageSvg />
          </div>
        ) : (
          <div className={styles.cardImg}>
            <CardImageSvg />
          </div>
        )}
        <div className={styles.cardActions}>
          <p
            className={config.icon ? styles.cardStepsText2 : styles.cardStepsText}
            style={{ marginBottom: config.buttonText ? undefined : 0 }}
          >
            {config.message}
          </p>
          {config.buttonText && config.buttonAction && (
            <Button
              className={styles.cardOpenButton}
              type="primary"
              onClick={config.buttonAction}
            >
              {config.buttonText}
            </Button>
          )}
        </div>
      </Flex>
    </div>
  )

  const renderFeatureDisabled = () => (
    <div className={styles.issueCard}>
      <Flex align="center" gap={25}>
        <div className={styles.cardImgSoloIcon}>🕐</div>
        <div className={styles.cardActions}>
          <p style={{ marginBottom: 0 }} className={styles.cardStepsText2}>
            Dear customer, we are currently in the private beta stage. Please contact{' '}
            <a href="mailto:beta@godefi.me">beta@godefi.me</a> to request access.
          </p>
        </div>
      </Flex>
    </div>
  )

  const renderCardsList = () => (
    <Splide
      aria-label="Cards list"
      ref={sliderRef}
      options={{
        arrows: false,
        pagination: false,
        waitForTransition: true,
        perPage: 1,
        perMove: 1,
        speed: 500,
        direction: 'ltr',
        padding: 16,
        gap: 8,
      }}
    >
      {filteredCards.map((card) => (
        <SplideSlide key={card.id} onClick={() => handleContextMenu(card)}>
          <div className={styles.card}>
            <Flex justify="space-between" align="center">
              <div>
                {card.name ? <p className={styles.cardName}>{card.name}</p> : <p className={styles.cardName}>GoDefi card</p>}
                <p className={styles.balance}>
                  Spendable balance
                  <span>{formatUsdAmount(card.balance.amount)}</span>
                </p>
              </div>
              <div className={styles.cardData}>
                <span className={styles.cardNumber}>*{card.panMasked.slice(-4)}</span>
                <span className={styles.cardToken}>
                  <ImageWithFallback
                    src={getCdnImageWithPath(card.balance.imageUrl, 'icon.svg')}
                    fallback={getCdnImageWithPath('/currencies/none', 'icon.svg')}
                    alt={card.balance.symbol}
                  />
                </span>
                <CardImageBig />
              </div>
            </Flex>
          </div>
        </SplideSlide>
      ))}
    </Splide>
  )

  if (isLoading) {
    return <MobileSkeletonLoader withTitle height="110px" width="100%" />
  }

  const cardStateConfig = getCardStateConfig()

  return (
    <div className={styles.root}>
      <MobileBlockHeader
        title="Linked cards"
        viewAllTitle={filteredCards.length === 0 ? null : 'View all'}
        link={AppRoutes.CARDS_PAGE}
        withMargin
      />

      {isFeatureDisabled && renderFeatureDisabled()}

      {!isFeatureDisabled && (
        <>
          {cardStateConfig && renderCardState(cardStateConfig)}
          {filteredCards.length > 0 && renderCardsList()}
        </>
      )}
    </div>
  )
}
